<script setup lang="ts">
import { reactive, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import { ServiceError } from "@core/exceptions"
import { useFetchWrapper } from "@core/composables/useFetchWrapper"
import { useAlarmeStore } from "@modules/energy-manager/stores/alarme.store"
import type { AlarmConfig } from "@modules/energy-manager/types/alarme"

const props = defineProps<{
    type: "building" | "source"
    itemId: string
    variable: string
    alarm?: Record<string, boolean | number | string>
}>()

const alarmeStore = useAlarmeStore()
const { t } = useI18n({
    messages: {
        fr: {
            alarm: {
                alarmOptionsTitle: "Paramètres de l'alarme :",
                alarmOptionsSectionThresholds:
                    "Déclencher l'alarme si les valeurs dépassent :",
                alarmOptionsSectionNotifications:
                    "Activer les notifications par email quand :",
                optionHighActive: "La limite haute",
                optionLowActive: "La limite basse",
                optionNotifyResolved: "L'alarme est résolue",
                optionNotifyTriggered: "L'alarme se déclenche",
                optionsUpdateError:
                    "La mise à jour des options d’alarme a échoué.",
            },
        },
    },
})

/**
 * Copie locale des 4 options (seule source de vérité pour les switches).
 * Initialisée / réinitialisée depuis le store Pinia (building / source).
 */
const localOptions = reactive({
    high_active: false,
    low_active: false,
    notify_resolved: false,
    notify_triggered: false,
})

function alarmTarget() {
    return { scope: props.type, id: props.itemId } as const
}

function toAlarmBool(value: unknown): boolean {
    if (value === true || value === 1) return true
    if (value === false || value === 0) return false
    if (typeof value === "string") {
        const s = value.toLowerCase()
        if (s === "true" || s === "1") return true
        if (s === "false" || s === "0") return false
    }
    return Boolean(value)
}

/** Lit `alarme[variable]` dans le store et remplit la copie locale. */
function hydrateLocalFromStore() {
    const cfg = alarmeStore.getAlarmConfigFromClient(
        alarmTarget(),
        props.variable
    )
    localOptions.high_active = toAlarmBool(cfg.high_active)
    localOptions.low_active = toAlarmBool(cfg.low_active)
    localOptions.notify_resolved = toAlarmBool(cfg.notify_resolved)
    localOptions.notify_triggered = toAlarmBool(cfg.notify_triggered)
}

watch(
    () => [props.type, props.itemId, props.variable] as const,
    () => {
        hydrateLocalFromStore()
    },
    { immediate: true }
)

type OptionField = keyof Pick<
    AlarmConfig,
    "high_active" | "low_active" | "notify_resolved" | "notify_triggered"
>

function getOptionsPayload(): Pick<
    AlarmConfig,
    "high_active" | "low_active" | "notify_resolved" | "notify_triggered"
> {
    return {
        high_active: localOptions.high_active,
        low_active: localOptions.low_active,
        notify_resolved: localOptions.notify_resolved,
        notify_triggered: localOptions.notify_triggered,
    }
}

const { fetch: persistAlarmOptions, error: persistAlarmOptionsError } =
    useFetchWrapper(async () => {
        await alarmeStore.updateAlarm(
            alarmTarget(),
            props.variable,
            getOptionsPayload()
        )
    })

const optionsErrorMessage = ref("")
let optionsPersistSeq = 0

async function onOptionToggle(field: OptionField, next: boolean) {
    const snapshot = { ...getOptionsPayload() }

    if (field === "high_active") localOptions.high_active = next
    else if (field === "low_active") localOptions.low_active = next
    else if (field === "notify_resolved") localOptions.notify_resolved = next
    else localOptions.notify_triggered = next

    optionsErrorMessage.value = ""
    const mySeq = ++optionsPersistSeq
    await persistAlarmOptions(undefined, { silent: true })

    if (!persistAlarmOptionsError.value) {
        return
    }
    if (mySeq !== optionsPersistSeq) {
        return
    }

    const err = persistAlarmOptionsError.value
    optionsErrorMessage.value =
        err instanceof ServiceError
            ? err.message
            : t("alarm.optionsUpdateError")

    Object.assign(localOptions, snapshot)
}
</script>

<template>
    <div class="flex flex-col gap-4">
        <BaseMessage
            v-if="optionsErrorMessage"
            severity="error"
            :closable="true"
            @close="optionsErrorMessage = ''"
        >
            {{ optionsErrorMessage }}
        </BaseMessage>

        <h4 class="text-base font-semibold text-primary">
            {{ t("alarm.alarmOptionsTitle") }}
        </h4>

        <div class="flex flex-wrap gap-12">
            <section class="flex flex-col gap-3">
                <p class="text-sm font-medium text-slate-800">
                    {{ t("alarm.alarmOptionsSectionThresholds") }}
                </p>
                <div class="flex flex-col gap-3 sm:max-w-md">
                    <BaseInputWrapper
                        input-id="alarm-opt-high-active"
                        :label="t('alarm.optionHighActive')"
                        unfloat
                        orientation="horizontal"
                    >
                        <BaseToggleSwitch
                            :model-value="localOptions.high_active"
                            @update:model-value="
                                onOptionToggle('high_active', Boolean($event))
                            "
                        />
                    </BaseInputWrapper>
                    <BaseInputWrapper
                        input-id="alarm-opt-low-active"
                        :label="t('alarm.optionLowActive')"
                        unfloat
                        orientation="horizontal"
                    >
                        <BaseToggleSwitch
                            :model-value="localOptions.low_active"
                            @update:model-value="
                                onOptionToggle('low_active', Boolean($event))
                            "
                        />
                    </BaseInputWrapper>
                </div>
            </section>

            <section class="flex flex-col gap-3">
                <p class="text-sm font-medium text-slate-800">
                    {{ t("alarm.alarmOptionsSectionNotifications") }}
                </p>
                <div class="flex flex-col gap-3 sm:max-w-md">
                    <BaseInputWrapper
                        input-id="alarm-opt-notify-resolved"
                        :label="t('alarm.optionNotifyResolved')"
                        unfloat
                        orientation="horizontal"
                    >
                        <BaseToggleSwitch
                            :model-value="localOptions.notify_resolved"
                            @update:model-value="
                                onOptionToggle(
                                    'notify_resolved',
                                    Boolean($event)
                                )
                            "
                        />
                    </BaseInputWrapper>
                    <BaseInputWrapper
                        input-id="alarm-opt-notify-triggered"
                        :label="t('alarm.optionNotifyTriggered')"
                        unfloat
                        orientation="horizontal"
                    >
                        <BaseToggleSwitch
                            :model-value="localOptions.notify_triggered"
                            @update:model-value="
                                onOptionToggle(
                                    'notify_triggered',
                                    Boolean($event)
                                )
                            "
                        />
                    </BaseInputWrapper>
                </div>
            </section>
        </div>
    </div>
</template>
